import { extractRoles } from '@/utils/routeGuards'
import { Box, Center, Grid, Group, Menu, Text } from '@mantine/core'
import { Link, useRouteContext, useRouter } from '@tanstack/react-router'
import React, { Fragment } from 'react'
import { CkdLogo } from './logo'
import { MenuGroup, MenuItemWithAllowedRoles, Menus, MenuSection } from './menu'

interface HeaderMenuProps {
  menus: Menus
}

const HeaderMenu: React.FC<HeaderMenuProps> = ({ menus }) => {
  const router = useRouter()
  const context = useRouteContext({ from: '/_auth' })

  const handleMenuItemClick = (path: string) => {
    router.navigate({
      to: path,
    })
  }

  const renderMenuItem = (item: MenuItemWithAllowedRoles) => {
    let hasPermission = true
    const roles = extractRoles(item.allowedRoles || [])
    if (context.auth && roles) {
      hasPermission = context.auth.hasRequiredRole(roles)
    }
    return hasPermission ? (
      <Menu.Item
        key={item.path}
        leftSection={item.leftSection}
        onClick={() => handleMenuItemClick(item.path)}
        py="8"
        ml="8"
      >
        {item.label}
      </Menu.Item>
    ) : null
  }

  const renderMenuSection = (section: MenuSection) => {
    const sectionLabel = section.label ? (
      <Menu.Label key={Math.random()} style={{ color: '#000000' }}>
        <Text fw={800} fs="italic" bg="#FaFaFa">
          {section.label}
        </Text>
      </Menu.Label>
    ) : null
    // check for permission in each child
    let hasPermission = false
    if (context.auth) {
      section.menus.forEach((item) => {
        const roles = extractRoles(item.allowedRoles || [])
        if (roles) {
          hasPermission = hasPermission || context.auth!.hasRequiredRole(roles)
        }
      })
    }
    if (!hasPermission) {
      return null
    }

    const subMenus = section.menus.map((item) => {
      return renderMenuItem(item)
    })

    return (
      <Fragment key={Math.random()}>
        {sectionLabel}
        {subMenus}
      </Fragment>
    )
  }

  /**
   * Renders a single column dropdown menu layout.
   * All sections are stacked vertically with dividers between them.
   */
  const renderSingleColumnMenu = (group: MenuGroup) => {
    const sections = group.sections
      .map((section) => renderMenuSection(section))
      .filter((section) => section !== null)

    if (sections.length === 0) {
      return null
    }
    // add divider between sections
    const sectionsWithDivider = sections.reduce((acc, section, index) => {
      if (index === 0) {
        return [section]
      }
      return [...acc, <Menu.Divider key={Math.random()} />, section]
    }, [] as React.ReactNode[])

    return (
      <Menu
        key={group.label}
        position="bottom-start"
        shadow="md"
        trigger="hover"
      >
        <Menu.Target>
          <Group gap="xs" align="center" style={{ cursor: 'default' }}>
            <Box mt="4">{group.leftSection}</Box>
            <Text c="#FFFFFF">{group.label}</Text>
          </Group>
        </Menu.Target>
        {sections && <Menu.Dropdown>{sectionsWithDivider}</Menu.Dropdown>}
      </Menu>
    )
  }

  type Section = {
    right?: MenuSection
    left?: MenuSection
    single?: MenuSection
  }
  /**
   * Renders a dynamic two-column dropdown menu layout.
   * Uses complex logic to pair sections into left/right columns dynamically.
   * Handles mixed single and paired sections.
   */
  const renderDynamicTwoColumnMenu = (group: MenuGroup) => {
    const renderedSections: Array<Section> = []
    let currentSection: Section = {}
    group.sections.forEach((section) => {
      const isLeft = section.column === 'left'
      const isRight = section.column === 'right'
      if (currentSection.left && currentSection.right) {
        renderedSections.push(currentSection)
        currentSection = {}
      }
      if (isLeft) {
        if (currentSection.left) {
          renderedSections.push(currentSection)
          currentSection = {}
        }
        currentSection.left = section
      } else if (isRight) {
        if (currentSection.right) {
          renderedSections.push(currentSection)
          currentSection = {}
        }
        currentSection.right = section
      } else {
        if (currentSection.left || currentSection.right) {
          renderedSections.push(currentSection)
          currentSection = {}
        }
        renderedSections.push({ single: section })
      }
    })
    if (currentSection.left || currentSection.right) {
      renderedSections.push(currentSection)
    }

    const sections = renderedSections
      .map((section: Section) => {
        if (section.left || section.right) {
          return (
            <Grid>
              <Grid.Col span={6}>
                {section.left && renderMenuSection(section.left)}
              </Grid.Col>
              <Grid.Col span={6}>
                {section.right && renderMenuSection(section.right)}
              </Grid.Col>
            </Grid>
          )
        }
        if (section.single) return renderMenuSection(section.single)
      })
      .filter((section) => section !== null)

    if (sections.length === 0) {
      return null
    }
    // add divider between sections
    const sectionsWithDivider = sections.reduce((acc, section, index) => {
      if (index === 0) {
        return [section]
      }
      return [...acc, <Menu.Divider key={Math.random()} />, section]
    }, [] as React.ReactNode[])

    return (
      <Menu
        key={group.label}
        position="bottom-start"
        shadow="md"
        trigger="hover"
      >
        <Menu.Target>
          <Group gap="xs" align="center" style={{ cursor: 'default' }}>
            <Box mt="4">{group.leftSection}</Box>
            <Text c="#FFFFFF">{group.label}</Text>
          </Group>
        </Menu.Target>
        {sections && <Menu.Dropdown>{sectionsWithDivider}</Menu.Dropdown>}
      </Menu>
    )
  }

  /**
   * Renders a fixed two-column dropdown menu layout.
   * Sections are organized into left and right columns based on section.column property.
   * Falls back to left column if no column is specified.
   */
  const renderTwoColumnMenu = (group: MenuGroup) => {
    const lefts: Array<MenuSection> = []
    const rights: Array<MenuSection> = []
    group.sections.forEach((section) => {
      if (section.column === 'right') rights.push(section)
      else lefts.push(section)
    })

    const leftSections = lefts
      .map((section) => renderMenuSection(section))
      .filter((section) => section !== null)

    const rightSections = rights
      .map((section) => renderMenuSection(section))
      .filter((section) => section !== null)

    if (leftSections.length === 0 && rightSections.length === 0) {
      return null
    }

    // add divider between sections
    const leftSectionsWithDivider = leftSections.reduce(
      (acc, section, index) => {
        if (index === 0) {
          return [section]
        }
        return [...acc, <Menu.Divider key={Math.random()} />, section]
      },
      [] as React.ReactNode[]
    )

    const rightSectionsWithDivider = rightSections.reduce(
      (acc, section, index) => {
        if (index === 0) {
          return [section]
        }
        return [...acc, <Menu.Divider key={Math.random()} />, section]
      },
      [] as React.ReactNode[]
    )

    return (
      <Menu
        key={group.label}
        position="bottom-start"
        shadow="md"
        trigger="hover"
      >
        <Menu.Target>
          <Group gap="xs" align="center" style={{ cursor: 'default' }}>
            <Box mt="4">{group.leftSection}</Box>
            <Text c="#FFFFFF">{group.label}</Text>
          </Group>
        </Menu.Target>
        <Menu.Dropdown>
          {leftSectionsWithDivider.length > 0 &&
            rightSectionsWithDivider.length > 0 && (
              <Grid miw={200}>
                <Grid.Col span={6}>{leftSectionsWithDivider}</Grid.Col>
                <Grid.Col span={6}>{rightSectionsWithDivider}</Grid.Col>
              </Grid>
            )}
          {leftSectionsWithDivider.length > 0 &&
            rightSectionsWithDivider.length === 0 && (
              <>{leftSectionsWithDivider}</>
            )}
        </Menu.Dropdown>
      </Menu>
    )
  }

  /**
   * Renders a fixed three-column dropdown menu layout.
   * Sections are organized into left, middle, and right columns based on section.column property.
   * Currently used by warehouse menu for complex navigation structure.
   */
  const renderThreeColumnMenu = (group: MenuGroup) => {
    const lefts: Array<MenuSection> = []
    const middles: Array<MenuSection> = []
    const rights: Array<MenuSection> = []

    group.sections.forEach((section) => {
      if (section.column === 'left') lefts.push(section)
      else if (section.column === 'middle') middles.push(section)
      else if (section.column === 'right') rights.push(section)
      else lefts.push(section) // default to left for sections without column specification
    })

    const leftSections = lefts
      .map((section) => renderMenuSection(section))
      .filter((section) => section !== null)

    const middleSections = middles
      .map((section) => renderMenuSection(section))
      .filter((section) => section !== null)

    const rightSections = rights
      .map((section) => renderMenuSection(section))
      .filter((section) => section !== null)

    if (
      leftSections.length === 0 &&
      middleSections.length === 0 &&
      rightSections.length === 0
    ) {
      return null
    }

    // add divider between sections
    const leftSectionsWithDivider = leftSections.reduce(
      (acc, section, index) => {
        if (index === 0) {
          return [section]
        }
        return [...acc, <Menu.Divider key={Math.random()} />, section]
      },
      [] as React.ReactNode[]
    )

    const middleSectionsWithDivider = middleSections.reduce(
      (acc, section, index) => {
        if (index === 0) {
          return [section]
        }
        return [...acc, <Menu.Divider key={Math.random()} />, section]
      },
      [] as React.ReactNode[]
    )

    const rightSectionsWithDivider = rightSections.reduce(
      (acc, section, index) => {
        if (index === 0) {
          return [section]
        }
        return [...acc, <Menu.Divider key={Math.random()} />, section]
      },
      [] as React.ReactNode[]
    )

    const hasAnyColumn =
      leftSectionsWithDivider.length > 0 ||
      middleSectionsWithDivider.length > 0 ||
      rightSectionsWithDivider.length > 0

    return (
      <Menu
        key={group.label}
        position="bottom-start"
        shadow="md"
        trigger="hover"
      >
        <Menu.Target>
          <Group gap="xs" align="center" style={{ cursor: 'default' }}>
            <Box mt="4">{group.leftSection}</Box>
            <Text c="#FFFFFF">{group.label}</Text>
          </Group>
        </Menu.Target>
        <Menu.Dropdown>
          {hasAnyColumn && (
            <Grid miw={300}>
              <Grid.Col span={4}>{leftSectionsWithDivider}</Grid.Col>
              <Grid.Col span={4}>{middleSectionsWithDivider}</Grid.Col>
              <Grid.Col span={4}>{rightSectionsWithDivider}</Grid.Col>
            </Grid>
          )}
        </Menu.Dropdown>
      </Menu>
    )
  }
  /**
   * Render each menu group with its specified layout type.
   * Each menu can specify its own layoutType to control dropdown structure:
   * - Most menus use default 'two-column' layout
   * - Warehouse uses 'three-column' for complex navigation
   * - Other layout types available for future customization
   */
  const menuGroups = menus.map((group) => {
    const layoutType = group.layoutType || 'two-column' // Default to two-column layout
    if (layoutType === 'single') return renderSingleColumnMenu(group)
    else if (layoutType === 'dynamic-two-column')
      return renderDynamicTwoColumnMenu(group)
    else if (layoutType === 'two-column') return renderTwoColumnMenu(group)
    else if (layoutType === 'three-column') return renderThreeColumnMenu(group)
    return renderTwoColumnMenu(group) // fallback
  })

  return (
    <Group gap="0" mt="6">
      <Center mt="12" px="8" ml="10" visibleFrom="xs">
        <Link to="/">
          <CkdLogo width={55} color="#37AFE1" />
        </Link>
      </Center>
      <Group gap="md" align="center" mt="10" ml="10">
        {menuGroups}
      </Group>
    </Group>
  )
}

export default HeaderMenu
